import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, RotateCcw, Trash2 } from 'lucide-react';
import { ApiError, api } from '../../lib/api.js';
import { Badge, Button, Dialog, DialogContent, DialogFooter, Field, Input, Panel, Spinner, Textarea, useToast } from '../../ui/index.js';

interface Desk {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  archivedAt: string | null;
  archivedByName: string | null;
  archiveReason: string | null;
  tickets: number;
  openTickets: number;
}

interface Payload {
  desks: Desk[];
  retiredSources: { collection: string; source: string; tenantId: string; deskExists: boolean; retiredAt: string }[];
}

const when = (iso: string) => new Date(iso).toLocaleString();

/**
 * Platform > Desks: archive a desk (reversible), restore it, or delete an
 * archived one for good.
 *
 * ARCHIVE FIRST, ALWAYS. Delete is only offered for a desk already archived,
 * and asks for its id to be typed, so a desk cannot go from working to gone
 * in one click.
 */
export function DesksPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['platform-desks'], queryFn: () => api.get<Payload>('/api/v1/platform/desks') });
  const [archiving, setArchiving] = useState<Desk | null>(null);
  const [deleting, setDeleting] = useState<Desk | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['platform-desks'] });
    // The desk switcher and everyone's access come from here.
    void qc.invalidateQueries({ queryKey: ['me'] });
  };
  const restore = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/platform/desks/${id}/restore`, {}),
    onSuccess: (_d, id) => {
      refresh();
      toast.success('Desk restored', `${id} is back exactly as it was.`);
    },
    onError: (e) => toast.error('Not restored', e instanceof ApiError ? e.message : undefined),
  });

  if (q.isLoading) return <Spinner />;
  if (q.error || !q.data) return <p className="text-xs text-ink-tertiary">The desks could not be loaded.</p>;

  const active = q.data.desks.filter((d) => d.active);
  const archived = q.data.desks.filter((d) => !d.active && d.archivedAt);
  const leftInIndex = q.data.retiredSources.filter((r) => !r.deskExists);

  return (
    <div className="space-y-4">
      <Panel title="Desks" subtitle="Archiving a desk hides it and stops it; nothing is removed, and it can be restored.">
        <table className="w-full text-left text-xs">
          <thead className="text-2xs uppercase tracking-wide text-ink-tertiary">
            <tr className="border-b border-line">
              <th className="py-1.5 font-medium">Desk</th>
              <th className="py-1.5 font-medium">Tickets</th>
              <th className="py-1.5 font-medium">Open</th>
              <th className="py-1.5" />
            </tr>
          </thead>
          <tbody>
            {active.map((d) => (
              <tr key={d.id} className="border-b border-line-subtle">
                <td className="py-2">
                  <span className="font-medium text-ink">{d.name}</span>{' '}
                  <span className="text-ink-tertiary">{d.id}</span>
                </td>
                <td className="nums py-2 text-ink-secondary">{d.tickets}</td>
                <td className="nums py-2 text-ink-secondary">{d.openTickets}</td>
                <td className="py-2 text-right">
                  <Button size="xs" variant="ghost" leading={<Archive />} onClick={() => setArchiving(d)}>
                    Archive
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel
        title="Archived"
        subtitle="Hidden from everyone, reading no mail and sending none. Restore puts a desk back exactly as it was."
      >
        {archived.length === 0 ? (
          <p className="text-xs text-ink-tertiary">No archived desks.</p>
        ) : (
          <ul className="space-y-2">
            {archived.map((d) => (
              <li key={d.id} className="rounded-md border border-line-subtle p-2.5 text-xs">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium text-ink">{d.name}</span>
                  <span className="text-ink-tertiary">{d.id}</span>
                  <Badge tone="neutral">{d.tickets} tickets</Badge>
                </div>
                <p className="mt-0.5 text-2xs text-ink-secondary">
                  Archived {when(d.archivedAt!)}
                  {d.archivedByName ? ` by ${d.archivedByName}` : ''}
                  {d.archiveReason ? ` — ${d.archiveReason}` : ''}
                </p>
                <div className="mt-2 flex gap-1.5">
                  <Button
                    size="xs"
                    variant="secondary"
                    leading={<RotateCcw />}
                    loading={restore.isPending}
                    onClick={() => restore.mutate(d.id)}
                  >
                    Restore
                  </Button>
                  <Button size="xs" variant="ghost" leading={<Trash2 />} className="text-danger" onClick={() => setDeleting(d)}>
                    Delete permanently
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {leftInIndex.length > 0 && (
        <Panel
          title="Left in the search index"
          subtitle="Deleted desks' knowledgebase namespaces. They are never suggested, but the index still holds them until someone with the index admin token purges them."
        >
          <ul className="space-y-1 text-2xs">
            {leftInIndex.map((r) => (
              <li key={`${r.collection}/${r.source}`} className="font-mono text-ink-secondary">
                {r.collection}/{r.source}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {archiving && <ArchiveDialog desk={archiving} onClose={() => setArchiving(null)} onDone={refresh} />}
      {deleting && <DeleteDialog desk={deleting} onClose={() => setDeleting(null)} onDone={refresh} />}
    </div>
  );
}

function ArchiveDialog({ desk, onClose, onDone }: { desk: Desk; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const go = useMutation({
    mutationFn: () => api.post(`/api/v1/platform/desks/${desk.id}/archive`, { reason }),
    onSuccess: () => {
      onDone();
      onClose();
      toast.success(`${desk.name} archived`, 'Restore it from Archived at any time.');
    },
    onError: (e) => toast.error('Not archived', e instanceof ApiError ? e.message : undefined),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Archive ${desk.name}`}
        description={`Everyone loses sight of it, it stops reading its mailbox and sends nothing; its ${desk.tickets} tickets and all its settings are kept. Its knowledgebase stops being suggested anywhere.`}
      >
        <Field label="Why" required hint="Recorded with the archive.">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Test desk, no longer needed" />
        </Field>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => go.mutate()} loading={go.isPending} disabled={reason.trim().length < 3}>
            Archive
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ desk, onClose, onDone }: { desk: Desk; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [typed, setTyped] = useState('');
  const go = useMutation({
    mutationFn: () => api.del(`/api/v1/platform/desks/${desk.id}`, { confirm: typed }),
    onSuccess: () => {
      onDone();
      onClose();
      toast.success(`${desk.name} deleted`, 'The audit log keeps the record.');
    },
    onError: (e) => toast.error('Not deleted', e instanceof ApiError ? e.message : undefined),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Delete ${desk.name} permanently`}
        description={`This removes the desk and everything in it — ${desk.tickets} tickets, their conversations and files, its articles, settings and people's access. It cannot be undone. The audit log keeps a record that it happened.`}
      >
        <Field label={`Type ${desk.id} to confirm`} required>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={desk.id} aria-label="Desk id to confirm" />
        </Field>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => go.mutate()} loading={go.isPending} disabled={typed !== desk.id}>
            Delete permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
